import { useState } from "react";
import Icon1 from "@assets/icons/Icon";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import { dossiersApi } from "@services/api/dossiers";
import { showNotification } from "context/notification.service";
import type { ClientNote, Dossier } from "types/dossier";

interface Props {
  dossierId: string;
  notes: ClientNote[];
  /** Called with the refreshed dossier after a note is added or removed. */
  onUpdated: (dossier: Dossier) => void;
  /** Hide the "add note" affordance (read-only display). */
  readOnly?: boolean;
}

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function ClientNotesBanner({ dossierId, notes, onUpdated, readOnly }: Props) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const hasNotes = notes && notes.length > 0;

  const handleAdd = async () => {
    const trimmed = text.trim();
    if (!trimmed || !dossierId) return;
    setSaving(true);
    try {
      const updated = await dossiersApi.addNote(dossierId, trimmed);
      onUpdated(updated);
      setText("");
      setAdding(false);
      showNotification({ type: "success", message: "Note ajoutée" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'ajout de la note" });
    } finally {
      setSaving(false);
    }
  };

  const handleEditSave = async (noteId: string) => {
    const trimmed = editText.trim();
    if (!trimmed || !dossierId) return;
    setSavingEdit(true);
    try {
      const updated = await dossiersApi.updateNote(dossierId, noteId, trimmed);
      onUpdated(updated);
      setEditingId(null);
      setEditText("");
      showNotification({ type: "success", message: "Note modifiée" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la modification" });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (noteId: string) => {
    if (!dossierId) return;
    try {
      const updated = await dossiersApi.deleteNote(dossierId, noteId);
      onUpdated(updated);
      showNotification({ type: "success", message: "Note supprimée" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la suppression" });
    }
  };

  if (!dossierId) return null;
  if (!hasNotes && readOnly) return null;

  return (
    <div className="flex flex-col gap-1.5">
      {hasNotes && (
        <div
          className="flex flex-col gap-1.5 px-3 py-2 rounded-lg"
          style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
        >
          <div className="flex flex-row items-center gap-2">
            <Icon1 name="warning" size={14} color="#9CA3AF" />
            <span className="text-h3 font-semibold" style={{ color: "#374151" }}>
              Notes client ({notes.length})
            </span>
          </div>
          {notes.map((n) => (
            editingId === n.id ? (
              <div key={n.id} className="flex flex-col gap-2 p-2 rounded-lg bg-white border border-neutral-200">
                <AreaTextField
                  value={editText}
                  onChange={setEditText}
                  placeholder="Modifier la note…"
                  rows={2}
                  focusOnStart
                />
                <div className="flex flex-row justify-end gap-2">
                  <SecondaryButton variant="small" onClick={() => { setEditingId(null); setEditText(""); }}>
                    Annuler
                  </SecondaryButton>
                  <Button
                    variant="small"
                    label={savingEdit ? "Enregistrement…" : "Enregistrer"}
                    onClick={() => handleEditSave(n.id)}
                    disabled={savingEdit || !editText.trim()}
                  />
                </div>
              </div>
            ) : (
              <div key={n.id} className="flex flex-row items-start gap-2 group">
                <span className="text-h4 flex-1" style={{ color: "#4B5563", whiteSpace: "pre-wrap" }}>
                  {n.text}
                  <span className="text-h5 ml-2" style={{ color: "#9CA3AF" }}>
                    — {n.author_name}, {formatDate(n.created_at)}
                  </span>
                </span>
                {!readOnly && (
                  <div className="flex flex-row items-center gap-0.5 flex-shrink-0">
                    <button
                      className="rounded-full p-0.5 hover:bg-neutral-200 transition-colors"
                      onClick={() => { setEditingId(n.id); setEditText(n.text); }}
                      title="Modifier la note"
                    >
                      <Icon1 name="modify" size={14} color="#6B7280" />
                    </button>
                    <button
                      className="rounded-full p-0.5 hover:bg-neutral-200 transition-colors"
                      onClick={() => handleDelete(n.id)}
                      title="Supprimer la note"
                    >
                      <Icon1 name="effacer2" size={14} color="#6B7280" />
                    </button>
                  </div>
                )}
              </div>
            )
          ))}
        </div>
      )}

      {!readOnly && !adding && (
        <button
          className="self-start flex flex-row items-center gap-1 text-h4 font-medium px-2 py-0.5 rounded-lg hover:bg-neutral-100 transition-colors"
          style={{ color: "#6B7280" }}
          onClick={() => setAdding(true)}
        >
          <Icon1 name="plus" size={12} color="#6B7280" />
          Ajouter une note client
        </button>
      )}

      {!readOnly && adding && (
        <div className="flex flex-col gap-2 p-2 rounded-lg bg-white border border-neutral-200">
          <AreaTextField
            value={text}
            onChange={setText}
            placeholder="Ex. : litige avec le client sur la facture du 12/06…"
            rows={2}
            focusOnStart
          />
          <div className="flex flex-row justify-end gap-2">
            <SecondaryButton variant="small" onClick={() => { setAdding(false); setText(""); }}>
              Annuler
            </SecondaryButton>
            <Button
              variant="small"
              label={saving ? "Ajout…" : "Ajouter"}
              onClick={handleAdd}
              disabled={saving || !text.trim()}
            />
          </div>
        </div>
      )}
    </div>
  );
}
